import React, { useEffect, useState } from "react";
import { Save, KeyRound, IndianRupee, MessageSquare, MessageCircle, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function SuperAdminSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/super-admin/settings");
    setSettings(data);
    setForm({});
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // For masked fields - never overwrite unless user explicitly types something new
  const cur = (k) => (form[k] !== undefined ? form[k] : (settings?.[k] ?? ""));

  const save = async () => {
    if (Object.keys(form).length === 0) {
      toast.info("Nothing changed");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/super-admin/settings", form);
      toast.success("Settings saved");
      setSettings(data);
      setForm({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="sa-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Platform Settings</h1>
          <p className="text-sm text-slate-500">Configure master Razorpay credentials, OTP pricing and Pingbix provider.</p>
        </div>
        <button data-testid="save-settings" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-xl font-bold text-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <section className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-tenant-primary" />
          <h2 className="font-extrabold tracking-tight">Master Razorpay (Receives from Tenants)</h2>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-700 flex gap-2 items-start mb-4">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            Use <b>rzp_test_*</b> keys until you go live. Live keys (<b>rzp_live_*</b>) will charge real money for every recharge or subscription payment.
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Mode</Label>
            <select data-testid="razorpay_mode" value={cur("razorpay_mode")} onChange={(e) => set("razorpay_mode", e.target.value)} className="mt-1.5 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary bg-white">
              <option value="test">Test (rzp_test_*)</option>
              <option value="live">Live (rzp_live_*)</option>
            </select>
          </div>
          <Field testId="razorpay_key_id" label="Key ID" placeholder="rzp_test_xxxxxxxx" value={cur("razorpay_key_id")} onChange={(v) => set("razorpay_key_id", v)} />
          <Field testId="razorpay_key_secret" label="Key Secret" placeholder="Leave blank to keep current" value={cur("razorpay_key_secret")} onChange={(v) => set("razorpay_key_secret", v)} masked />
          <Field testId="razorpay_webhook_secret" label="Webhook Secret" placeholder="Leave blank to keep current" value={cur("razorpay_webhook_secret")} onChange={(v) => set("razorpay_webhook_secret", v)} masked />
        </div>
      </section>

      <section className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <IndianRupee className="w-4 h-4 text-emerald-600" />
          <h2 className="font-extrabold tracking-tight">OTP Pricing</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PriceField testId="sms_otp_price" label="Per SMS OTP (₹)" value={cur("sms_otp_price")} onChange={(v) => set("sms_otp_price", parseFloat(v) || 0)} />
          <PriceField testId="whatsapp_otp_price" label="Per WhatsApp OTP (₹)" value={cur("whatsapp_otp_price")} onChange={(v) => set("whatsapp_otp_price", parseFloat(v) || 0)} />
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Each OTP sent to a real (non-demo) phone debits the tenant's OTP wallet by this amount.</p>
      </section>

      <section className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-blue-600" />
          <h2 className="font-extrabold tracking-tight">Pingbix SMS Provider</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field testId="pingbix_sms_userid" label="User ID" value={cur("pingbix_sms_userid")} onChange={(v) => set("pingbix_sms_userid", v)} />
          <Field testId="pingbix_sms_password" label="Password" masked value={cur("pingbix_sms_password")} onChange={(v) => set("pingbix_sms_password", v)} />
          <Field testId="pingbix_sms_api_key" label="API Key" masked value={cur("pingbix_sms_api_key")} onChange={(v) => set("pingbix_sms_api_key", v)} />
          <Field testId="pingbix_sms_sender_id" label="Sender ID" value={cur("pingbix_sms_sender_id")} onChange={(v) => set("pingbix_sms_sender_id", v)} />
          <Field testId="pingbix_sms_entity_id" label="DLT Entity ID" value={cur("pingbix_sms_entity_id")} onChange={(v) => set("pingbix_sms_entity_id", v)} />
          <Field testId="pingbix_sms_template_id" label="DLT Template ID" value={cur("pingbix_sms_template_id")} onChange={(v) => set("pingbix_sms_template_id", v)} />
        </div>
      </section>

      <section className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle className="w-4 h-4 text-emerald-600" />
          <h2 className="font-extrabold tracking-tight">Pingbix WhatsApp Provider</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field testId="pingbix_wa_api_key" label="API Key" masked value={cur("pingbix_wa_api_key")} onChange={(v) => set("pingbix_wa_api_key", v)} />
          <Field testId="pingbix_wa_sender" label="Sender (with +91)" value={cur("pingbix_wa_sender")} onChange={(v) => set("pingbix_wa_sender", v)} />
          <Field testId="pingbix_wa_campaign_name" label="Campaign Name" value={cur("pingbix_wa_campaign_name")} onChange={(v) => set("pingbix_wa_campaign_name", v)} />
          <Field testId="pingbix_wa_template_name" label="Template Name" value={cur("pingbix_wa_template_name")} onChange={(v) => set("pingbix_wa_template_name", v)} />
        </div>
      </section>

      <section className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-violet-600" />
          <h2 className="font-extrabold tracking-tight">OTP Routing</h2>
        </div>
        <Field testId="otp_provider_priority" label="Provider Priority (comma-separated)" placeholder="PINGBIX_SMS,PINGBIX_WHATSAPP" value={cur("otp_provider_priority")} onChange={(v) => set("otp_provider_priority", v)} />
      </section>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{children}</label>
);

const Field = ({ label, value, onChange, testId, placeholder, masked }) => (
  <div>
    <Label>{label}</Label>
    <input
      data-testid={testId}
      type={masked ? "password" : "text"}
      autoComplete="new-password"
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary font-mono text-[12px]"
    />
  </div>
);

const PriceField = ({ label, value, onChange, testId }) => (
  <div>
    <Label>{label}</Label>
    <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-xl px-3 py-2 bg-white">
      <IndianRupee className="w-4 h-4 text-slate-400" />
      <input
        data-testid={testId}
        type="number"
        step="0.01"
        min="0"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 outline-none font-bold"
      />
    </div>
  </div>
);
